import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Form, 
  Button, 
  InputGroup, 
  Table, 
  Nav,
  Card,
  Badge,
  ListGroup,
  Spinner
} from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import swal from 'sweetalert';
import Loader from './LoaderModal';
import Side from './Sidebar';
import Top from './Topbar';
import C from '../lib/Config';
import '../App.css';

const EditInvoice = () => {
  const history = useHistory();
  const { invoiceNumber } = useParams();
  
  // List of consultants
  const consultants = [
    'Dr. Rajesh Kumar', 'Dr. Priya Sharma', 'Dr. Amit Patel', 
    'Dr. Neha Gupta', 'Dr. Sanjay Mehta', 'Dr. Anita Singh', 
    'Dr. Vikram Reddy', 'Dr. Deepa Chatterjee', 'Dr. Rahul Khanna', 
    'Dr. Meena Iyer'
  ];

  // Department options
  const departments = ['OPD', 'IPD', 'Daycare', 'Pharmacy'];

  // Payment methods
  const paymentMethodOptions = [
    'Cash', 'UPI', 'Card', 'Cheque', 'Insurance', 
    'RTGS', 'MOCF', 'Writeoff', 'NEFT'
  ];

  // Discount reasons
  const discountReasons = [
    'Poor Patient', 
    'As per Consultant Doctor', 
    'As per Referring Doctor', 
    'C/o staff', 
    'Patient dr himself/herself', 
    'Staff'
  ];

  // State initialization

  const [errors, setErrors] = useState({
    invoiceDate: '',
    patientName: '',
    primaryConsultant: '',
    department: '',
    visitDate: '',
    admission_Date: ''
  });

  // Keep track of removed items and payment methods for deactivation
  const [removedItems, setRemovedItems] = useState([]);
  const [removedPayments, setRemovedPayments] = useState([]);
  const [vendors, setVendors] = useState([]);

  const [showPaymentValidation, setShowPaymentValidation] = useState(false);

  const [formState, setFormState] = useState({
    invoiceDate: '',
    visitDate: '',
    admission_Date: '',
    discharge_Date: '',
    patientName: '',
    patientId: '',
    primaryConsultant: '',
    department: '',
    vendor: '',
    comments: '',

    updatedBy: null, // Add this
    createdBy: null, // Add this
    instituteName: null, // Add this
    admission_Date: null, // Add this if needed
    discharge_Date: null, // Add this if needed
    
    // Filtering and selection states
    activeTab: 'services',
    category: '',
    searchTerm: '',

    discountType: 'percentage', 

    // Items and pricing
    selectedServices: [],
    selectedPackages: [],
    
    // Discount
    showDiscount: false,
    discountPercent: 0,
    discountAmount: '',
    discountReason: '',
    
    // Payment methods
    paymentMethods: [{ 
      method: '', 
      amount: '', 
      note: '', 
      date: '' 
    }],
    
    // Mode and loading
    isEditMode: true,
    loading: true,
    error: null
  });

  // Categories state
  const [serviceCategories, setServiceCategories] = useState([]);
  const [packageCategories, setPackageCategories] = useState([]);
  

  // Utility Functions
  const formatDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  // const getMinDate = () => {
  //   return formatDate(new Date());
  // };
  const getMinDate = () => {
    // Return a date far in the past to effectively remove the minimum date restriction
    return "1900-01-01"; // This effectively removes the restriction
  };
    
  // Fetch items for an invoice
  const fetchItems = async (invoiceNumber) => {
    try {
      const response = await fetch(`${C.getUrl()}/invoice/item/getByInvoiceNumber/${invoiceNumber}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch invoice items');
      }
      
      const apiResponse = await response.json();
      
      if (apiResponse.httpStatusCode === 200 && apiResponse.subErrors && apiResponse.subErrors.length > 0) {
        let invoiceData;
        try {
          invoiceData = JSON.parse(apiResponse.subErrors[0]);
          return invoiceData.itemList || [];
        } catch (e) {
          console.error('Error parsing items data:', e);
          return [];
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching items:', error);
      return [];
    }
  };

  const recalculateItemCharges = (items, index, showGlobalDiscount, globalDiscountPercent) => {
    const item = items[index];
    const price = item.item.price || 0;
    const quantity = item.item.quantity || 1;
    const baseTotal = price * quantity;
    
    // First apply item-specific discount
    let discountAmount = 0;
    if (item.item.discount) {
      // Default to percentage if discountType is not set
      const discountType = item.item.discountType || 'percentage';
      
      if (discountType === 'percentage') {
        discountAmount = (baseTotal * item.item.discount) / 100;
      } else {
        // Fixed amount discount
        discountAmount = Math.min(item.item.discount, baseTotal); // Can't discount more than total
      }
    }
    
    // Calculate final charges after item-specific discount ONLY
    // Important: We don't apply the global discount here, that's done in calculateNetAmount
    item.item.charges = baseTotal - discountAmount;
    
    // Ensure charges doesn't go negative
    if (item.item.charges < 0) item.item.charges = 0;
    
    return item;
  };

  const handleItemDiscountTypeChange = (index, type) => {
    setFormState(prev => {
      const updatedServices = [...prev.selectedServices];
      
      // Initialize item.discountType if not already set
      if (!updatedServices[index].item.discountType) {
        updatedServices[index].item = {
          ...updatedServices[index].item,
          discountType: type
        };
      } else {
        // Update the existing discount type
        updatedServices[index].item.discountType = type;
      }
      
      // If switching between types, recalculate discount amount or percentage
      const price = updatedServices[index].item.price || 0;
      const quantity = updatedServices[index].item.quantity || 1;
      const discountValue = updatedServices[index].item.discount || 0;
      
      // Convert discount based on the new type
      if (type === 'percentage') {
        // If changing to percentage, calculate percentage from fixed amount
        if (price * quantity > 0) {
          updatedServices[index].item.discount = (discountValue / (price * quantity)) * 100;
        }
      } else {
        // If changing to fixed, calculate fixed amount from percentage
        updatedServices[index].item.discount = (price * quantity * discountValue) / 100;
      }
      
      // Recalculate charges (important: only apply item discount, not global)
      recalculateItemCharges(updatedServices, index, false, 0);
      
      return {
        ...prev,
        selectedServices: updatedServices
      };
    });
  };

  const handleDiscountTypeChange = (type) => {
    setFormState(prev => {
      // First calculate the subtotal after item-specific discounts
      let subtotal = 0;
      prev.selectedServices.forEach(service => {
        const price = service.item.price || 0;
        const quantity = service.item.quantity || 1;
        const baseTotal = price * quantity;
        let itemTotal = baseTotal;
        
        // Apply item-specific discount
        if (service.item.discount) {
          const discountType = service.item.discountType || 'percentage';
          if (discountType === 'percentage') {
            const discountAmount = (baseTotal * service.item.discount) / 100;
            itemTotal = baseTotal - discountAmount;
          } else {
            itemTotal = baseTotal - Math.min(service.item.discount, baseTotal);
          }
        }
        
        // Add tax if applicable
        const taxRate = parseFloat(service.item.tax) || 0;
        if (taxRate > 0) {
          itemTotal += (itemTotal * taxRate) / 100;
        }
        
        subtotal += itemTotal;
      });
      
      // Keep both discount values and convert between them as needed
      let discountPercent = prev.discountPercent;
      let discountAmount = prev.discountAmount;
      
      // Calculate the equivalent value when switching types
      if (type === 'percentage' && prev.discountType === 'fixed') {
        // Convert fixed amount to percentage
        if (subtotal > 0 && discountAmount) {
          discountPercent = ((parseFloat(discountAmount) / subtotal) * 100).toFixed(2);
        }
      } else if (type === 'fixed' && prev.discountType === 'percentage') {
        // Convert percentage to fixed amount
        if (discountPercent) {
          discountAmount = ((parseFloat(discountPercent) / 100) * subtotal).toFixed(2);
        }
      }
      
      return {
        ...prev,
        discountType: type,
        discountPercent: discountPercent,
        discountAmount: discountAmount
      };
    });
  };
  
  // Fetch payments for an invoice
  const fetchPayments = async (invoiceNumber) => {
    try {
      const response = await fetch(`${C.getUrl()}/invoice/payment/getByInvoiceNumber/${invoiceNumber}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch invoice payments');
      }
      
      const apiResponse = await response.json();
      
      if (apiResponse.httpStatusCode === 200 && apiResponse.subErrors && apiResponse.subErrors.length > 0) {
        let invoiceData;
        try {
          invoiceData = JSON.parse(apiResponse.subErrors[0]);
          return invoiceData.paymentList || [];
        } catch (e) {
          console.error('Error parsing payments data:', e);
          return [];
        }
      }
      
      return [];
    } catch (error) {
      console.error('Error fetching payments:', error);
      return [];
    }
  };

  // Fetch all service invoices
  // const fetchAllServiceInvoices = async () => {
  //   try {
  //     const response = await fetch(`${C.getUrl()}/service-invoice/getAllServiceInvoices`, {
  //       method: 'GET',
  //       headers: { 'Content-Type': 'application/json' }
  //     });
  
  //     if (!response.ok) {
  //       throw new Error('Failed to fetch services');
  //     }
  
  //     const data = await response.json();
      
  //     if (data.subErrors && data.subErrors.length > 0) {
  //       const serviceItems = JSON.parse(data.subErrors[0]);
        
  //       const categorizedServices = serviceItems.reduce((acc, item) => {
  //         const categoryName = item.category;
  //         if (!acc[categoryName]) {
  //           acc[categoryName] = {
  //             name: categoryName,
  //             items: []
  //           };
  //         }
          
  //         acc[categoryName].items.push({
  //           name: item.item,
  //           price: item.costPerUnit
  //         });
          
  //         return acc;
  //       }, {});
  
  //       const serviceCategoriesList = Object.values(categorizedServices);
        
  //       setServiceCategories(serviceCategoriesList);
  //     } else {
  //       setServiceCategories([]);
        
  //       swal({
  //         title: "No Services",
  //         text: "No services are currently available.",
  //         icon: "info"
  //       });
  //     }
  //   } catch (error) {
  //     console.error('Error fetching services:', error);
  //     setServiceCategories([]);
      
  //     swal({
  //       title: "Error!",
  //       text: "Could not fetch services. Please try again later.",
  //       icon: "error"
  //     });
  //   }
  // };
  const fetchAllServiceInvoices = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/service-invoice/getAllServiceInvoices`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch services');
      }
  
      const data = await response.json();
      
      // Check if there are services
      if (data.subErrors && data.subErrors.length > 0) {
        // Parse the JSON string in subErrors
        const serviceItems = JSON.parse(data.subErrors[0]);
        
        // Group services by category
        const categorizedServices = serviceItems.reduce((acc, item) => {
          const categoryName = item.category;
          if (!acc[categoryName]) {
            acc[categoryName] = {
              name: categoryName,
              items: []
            };
          }
          
          // Store vendor information with each item
          // NOTE: Use "Vendor" with capital V as per your API response
          acc[categoryName].items.push({
            name: item.item,
            price: item.costPerUnit,
            vendor: item.Vendor || '' // Use "Vendor" with capital V
          });
          
          return acc;
        }, {});
  
        // Convert the grouped object to an array
        const serviceCategoriesList = Object.values(categorizedServices);
        setServiceCategories(serviceCategoriesList);
      } else {
        setServiceCategories([]);
        swal({
          title: "No Services",
          text: "No services are currently available.",
          icon: "info"
        });
      }
    } catch (error) {
      console.error('Error fetching services:', error);
      setServiceCategories([]);
      swal({
        title: "Error!",
        text: "Could not fetch services. Please try again later.",
        icon: "error"
      });
    }
  };

// Fetch vendors from API
const fetchVendors = async () => {
  console.log('Fetching vendors...');
  try {
    const url = `${C.getUrl()}/vendorMethod/getAllVendors`;
    console.log('API URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });

    console.log('Response status:', response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Error response:', errorText);
      throw new Error(`Failed to fetch vendors: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log('API Response:', data);
    
    let vendorsList = [];
    
    if (data.subErrors && data.subErrors.length > 0) {
      console.log('Found vendors in subErrors');
      vendorsList = JSON.parse(data.subErrors[0]);
    } else if (data.statusCode === 200 && data.message) {
      console.log('Found vendors in message');
      vendorsList = JSON.parse(data.message);
    } else if (Array.isArray(data)) {
      console.log('Direct array response');
      vendorsList = data;
    } else {
      console.warn('Unexpected API response format:', data);
      vendorsList = [];
    }
    
    console.log('Parsed vendors:', vendorsList);
    setVendors(vendorsList);
    
    if (vendorsList.length === 0) {
      console.warn('No vendor data found in response');
    }
  } catch (error) {
    console.error('Error fetching vendors:', error);
    setVendors([
      { id: 1, venderName: 'PULSE DIAGNOSTIC' },
      { id: 2, venderName: 'JARIWALA LAB' }
    ]);
    
    swal({
      title: "Error!",
      text: `Could not fetch vendors: ${error.message}`,
      icon: "error"
    });
  }
};
  // Fetch all package invoices
  const fetchAllPackageInvoices = async () => {
    try {
      const response = await fetch(`${C.getUrl()}/package-invoice/getAllPackageInvoices`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch packages');
      }
  
      const data = await response.json();
      
      if (data.subErrors && data.subErrors.length > 0) {
        const packageItems = JSON.parse(data.subErrors[0]);
        
        const categorizedPackages = packageItems.reduce((acc, item) => {
          const categoryName = item.category;
          if (!acc[categoryName]) {
            acc[categoryName] = {
              name: categoryName,
              items: []
            };
          }
          
          acc[categoryName].items.push({
            name: item.item,
            price: item.costPerUnit
          });
          
          return acc;
        }, {});
  
        const packageCategoriesList = Object.values(categorizedPackages);
        
        setPackageCategories(packageCategoriesList);
      } else {
        setPackageCategories([]);
        
        swal({
          title: "No Packages",
          text: "No packages are currently available.",
          icon: "info"
        });
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      setPackageCategories([]);
      
      swal({
        title: "Error!",
        text: "Could not fetch packages. Please try again later.",
        icon: "error"
      });
    }
  };

  const fetchInvoiceDetails = useCallback(async () => {
    try {
      const response = await fetch(`${C.getUrl()}/invoice/getByInvoiceNumber/${invoiceNumber}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
  
      if (!response.ok) {
        throw new Error('Failed to fetch invoice details');
      }
  
      const apiResponse = await response.json();
      console.log("Raw API response:", apiResponse);
      
      // Extract the JSON string from the message field and parse it
      let invoiceData;
      if (apiResponse.statusCode === 200 && apiResponse.message) {
        try {
          // Try parsing the message as a JSON string
          invoiceData = typeof apiResponse.message === 'string' 
            ? JSON.parse(apiResponse.message) 
            : apiResponse.message;
          
          console.log("Parsed invoice data:", invoiceData);
        } catch (parseError) {
          console.error('Error parsing invoice data:', parseError);
          throw new Error('Invalid invoice data format');
        }
      } else {
        throw new Error('No invoice data found');
      }
  
      // Utility function to convert date formats
      const convertToYYYYMMDD = (dateString) => {
        if (!dateString) return '';
        
        // If already in YYYY-MM-DD format, return as is
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateString;
        }
        
        // Convert from DD/MM/YYYY to YYYY-MM-DD
        if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
          const [day, month, year] = dateString.split('/');
          return `${year}-${month}-${day}`;
        }
        
        // Try parsing as a date object
        const date = new Date(dateString);
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
        
        return '';
      };
  
      // Get the items from the main response if available
      let selectedServices = [];
      if (invoiceData.itemList && invoiceData.itemList.length > 0) {
        selectedServices = invoiceData.itemList.map(item => {
          // Determine discount type based on itemPType/itemRType fields
          const isPercentageDiscount = item.itemPType === "%" && (item.itemP || item.itemP === 0);
          const isFixedDiscount = item.itemRType === "₹" && (item.itemR || item.itemR === 0);
          
          let discountType = 'percentage'; // Default discount type
          let discountValue = 0;
          
          if (isPercentageDiscount) {
            discountType = 'percentage';
            discountValue = parseFloat(item.itemP);
          } else if (isFixedDiscount) {
            discountType = 'fixed';
            discountValue = parseFloat(item.itemR);
          } else if (item.discount) {
            // Fallback to default discount field
            discountValue = parseFloat(item.discount);
          }
          
          // Calculate the item's charge based ONLY on its individual discount
          const basePrice = item.costPerUnit || 0;
          const quantity = item.quantity || 1;
          const baseTotal = basePrice * quantity;
          
          let itemDiscountAmount = 0;
          if (discountValue) {
            if (discountType === 'percentage') {
              itemDiscountAmount = (baseTotal * discountValue) / 100;
            } else {
              itemDiscountAmount = Math.min(discountValue, baseTotal);
            }
          }
          
          // Calculate item charge without applying global discount
          const itemCharge = baseTotal - itemDiscountAmount;
          
          return {
            itemId: item.id,
            name: item.category || 'Service',
            item: {
              name: item.item || item.category,
              price: item.costPerUnit,
              quantity: item.quantity || 1,
              tax: item.tax || 0,
              charges: itemCharge, // Set charges based on individual discount only
              discount: discountValue,
              discountType: discountType
            }
          };
        });
      }
  
      // Get payments from invoice data
      let paymentMethods = [];
      if (invoiceData.paymentList && invoiceData.paymentList.length > 0) {
        paymentMethods = invoiceData.paymentList.map(payment => ({
          paymentId: payment.id,
          method: payment.paymentMethod,
          amount: payment.paymentAmount.toString(),
          note: payment.otherNote || '',
          date: convertToYYYYMMDD(payment.paymentDate || invoiceData.invoiceDate)
        }));
      } else {
        paymentMethods = [{ method: '', amount: '', note: '', date: '' }];
      }
  
      // GLOBAL DISCOUNT: Determine type and value based on the specific fields
      console.log("Global discount fields:", {
        dpercentag: invoiceData.Dpercentag,
        discount: invoiceData.discount,
        invoicePType: invoiceData.invoicePType,
        invoiceRType: invoiceData.invoiceRType,
        invoiceR: invoiceData.invoiceR
      });
      
      let globalDiscountType = 'percentage';
      let globalDiscountPercent = 0;
      let globalDiscountAmount = 0;
      let hasDiscount = false;
      
      // Check for percentage discount
      if (invoiceData.invoicePType === "Percentage(%)" && (invoiceData.Dpercentag || invoiceData.Dpercentag === 0)) {
        globalDiscountType = 'percentage';
        globalDiscountPercent = parseFloat(invoiceData.Dpercentag) || 0;
        hasDiscount = globalDiscountPercent > 0;
        console.log("Using percentage discount:", globalDiscountPercent);
      } 
      // Check for fixed amount discount
      else if (invoiceData.invoiceRType === "Fixed amount" && (invoiceData.invoiceR || invoiceData.invoiceR === 0)) {
        globalDiscountType = 'fixed';
        globalDiscountAmount = parseFloat(invoiceData.invoiceR) || 0;
        hasDiscount = globalDiscountAmount > 0;
        console.log("Using fixed amount discount:", globalDiscountAmount);
      }
      // Fallback to dpercentag field
      else if (invoiceData.Dpercentag && parseFloat(invoiceData.Dpercentag) > 0) {
        globalDiscountType = 'percentage';
        globalDiscountPercent = parseFloat(invoiceData.Dpercentag);
        hasDiscount = true;
        console.log("Fallback to dpercentag:", globalDiscountPercent);
      }
      // Final fallback to discount field
      else if (invoiceData.discount && parseFloat(invoiceData.discount) > 0) {
        globalDiscountType = 'fixed';
        globalDiscountAmount = parseFloat(invoiceData.discount);
        hasDiscount = true;
        console.log("Fallback to discount:", globalDiscountAmount);
      }
      
      console.log("Final global discount determination:", {
        hasDiscount,
        globalDiscountType,
        globalDiscountPercent,
        globalDiscountAmount,
        discountReason: invoiceData.disResone
      });
      
      setFormState(prev => ({
        ...prev,
        invoiceId: invoiceData.invoiceId,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: convertToYYYYMMDD(invoiceData.invoiceDate), 
        // Conditionally set date fields based on department
        visitDate: invoiceData.department === 'OPD' ? convertToYYYYMMDD(invoiceData.visitDate) : null,
        admission_Date: invoiceData.department === 'IPD' ? convertToYYYYMMDD(invoiceData.admission_Date) : null, 
        discharge_Date: invoiceData.department === 'IPD' ? convertToYYYYMMDD(invoiceData.discharge_Date) : null,
        // If department is not IPD or OPD, default to visitDate for backward compatibility
        ...(invoiceData.department !== 'OPD' && invoiceData.department !== 'IPD' && invoiceData.visitDate && { 
          visitDate: convertToYYYYMMDD(invoiceData.visitDate)
        }),
        patientName: invoiceData.patientName,
        patientId: invoiceData.patientId,
        primaryConsultant: invoiceData.primaryConsultant,
        department: invoiceData.department,
        comments: invoiceData.comments || '',
        selectedServices: selectedServices,
        // Set global discount with correct type
        showDiscount: hasDiscount,
        discountType: globalDiscountType,
        discountPercent: globalDiscountType === 'percentage' ? globalDiscountPercent.toString() : '',
        discountAmount: globalDiscountType === 'fixed' ? globalDiscountAmount.toString() : '',
        discountReason: invoiceData.disResone || '',
        paymentMethods: paymentMethods,
        isEditMode: true,
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      setFormState(prev => ({ 
        ...prev, 
        error: error.message,
        loading: false 
      }));
      
      swal({
        title: "Error!",
        text: error.message,
        icon: "error"
      });
    }
  }, [invoiceNumber]);

  // Fetch services and packages on component mount
  useEffect(() => {
    fetchInvoiceDetails();
    fetchAllServiceInvoices();
    fetchAllPackageInvoices();
    fetchVendors();
  }, [fetchInvoiceDetails]);

  // State Update Handler
  const handleChange = (e) => {
    const { name, value } = e.target;

     // Special handling for vendor changes
 if (name === 'vendor') {
  console.log(`Vendor changed to: ${value}`); // For debugging
  setFormState(prev => ({
    ...prev,
    [name]: value,
    // Optionally reset other filters when changing vendor
    // category: '',  // Uncomment if you want to reset category when vendor changes
    // searchTerm: '' // Uncomment if you want to reset search when vendor changes
  }));
  return;
}
    
    // Special handling for department changes
    if (name === 'department') {
      if (value === 'IPD') {
        // For IPD, initialize admission and discharge dates if empty
        setFormState(prev => ({
          ...prev,
          [name]: value,
          // Set admission_Date to current visitDate if it exists, otherwise today's date
          admission_Date: prev.visitDate || formatDate(new Date()),
          // Discharge date can be empty initially
          discharge_Date: '',
          // Keep visitDate for internal tracking
          visitDate: prev.visitDate || formatDate(new Date())
        }));
      } else if (value === 'OPD') {
        // For OPD, ensure visitDate is set, but clear admission/discharge dates
        setFormState(prev => ({
          ...prev,
          [name]: value,
          visitDate: prev.visitDate || formatDate(new Date()),
          // Clear IPD related fields
          admission_Date: '',
          discharge_Date: ''
        }));
      } else {
        // For other departments, just update the department
        setFormState(prev => ({
          ...prev,
          [name]: value
        }));
      }
      return;
    }
    
    // Special handling for discount fields to auto-calculate
    if (name === 'discountPercent') {
      // Allow empty field but use 0 for calculations
      if (value === '') {
        setFormState(prev => ({
          ...prev,
          discountPercent: '',
          discountAmount: ''
        }));
        return;
      }
      
      // Handle invalid values
      if (isNaN(parseFloat(value))) {
        setFormState(prev => ({
          ...prev,
          discountPercent: '',
          discountAmount: ''
        }));
        return;
      }
      
      // Calculate subtotal after item-specific discounts AND tax
      let subtotal = 0;
      formState.selectedServices.forEach(service => {
        const price = service.item.price || 0;
        const quantity = service.item.quantity || 1;
        const baseTotal = price * quantity;
        let itemTotal = baseTotal;
        
        // Apply item-specific discount
        if (service.item.discount) {
          const discountType = service.item.discountType || 'percentage';
          if (discountType === 'percentage') {
            const discountAmount = (baseTotal * service.item.discount) / 100;
            itemTotal = baseTotal - discountAmount;
          } else {
            itemTotal = baseTotal - Math.min(service.item.discount, baseTotal);
          }
        }
        
        // Apply tax if applicable
        const taxRate = parseFloat(service.item.tax) || 0;
        if (taxRate > 0) {
          itemTotal += (itemTotal * taxRate) / 100;
        }
        
        subtotal += itemTotal;
      });
      
      // Now calculate the global discount amount based on the percentage
      const discountAmount = (parseFloat(value) / 100) * subtotal;
      
      setFormState(prev => ({
        ...prev,
        discountPercent: value,
        discountAmount: discountAmount.toFixed(2)
      }));
    } else if (name === 'discountAmount') {
      // Allow empty field but use 0 for calculations
      if (value === '') {
        setFormState(prev => ({
          ...prev,
          discountAmount: '',
          discountPercent: ''
        }));
        return;
      }
      
      // Handle invalid values
      if (isNaN(parseFloat(value))) {
        setFormState(prev => ({
          ...prev,
          discountAmount: '',
          discountPercent: ''
        }));
        return;
      }
      
      // Calculate subtotal after item-specific discounts AND tax
      let subtotal = 0;
      formState.selectedServices.forEach(service => {
        const price = service.item.price || 0;
        const quantity = service.item.quantity || 1;
        const baseTotal = price * quantity;
        let itemTotal = baseTotal;
        
        // Apply item-specific discount
        if (service.item.discount) {
          const discountType = service.item.discountType || 'percentage';
          if (discountType === 'percentage') {
            const discountAmount = (baseTotal * service.item.discount) / 100;
            itemTotal = baseTotal - discountAmount;
          } else {
            itemTotal = baseTotal - Math.min(service.item.discount, baseTotal);
          }
        }
        
        // Apply tax if applicable
        const taxRate = parseFloat(service.item.tax) || 0;
        if (taxRate > 0) {
          itemTotal += (itemTotal * taxRate) / 100;
        }
        
        subtotal += itemTotal;
      });
      
      // Avoid division by zero
      if (subtotal > 0) {
        const discountPercent = (parseFloat(value) / subtotal) * 100;
        
        setFormState(prev => ({
          ...prev,
          discountAmount: value,
          discountPercent: discountPercent.toFixed(2)
        }));
      } else {
        // If total amount is zero, just update the amount field
        setFormState(prev => ({
          ...prev,
          discountAmount: value
        }));
      }
    } else {
      // Default behavior for other fields
      setFormState(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };


  // Add this function to group packages by their headings
const renderPackageHeadings = () => {
  // Get original package categories
  let packageCats = packageCategories || [];
  
  // Apply filters if any
  if (formState.category) {
    packageCats = packageCats.filter(
      cat => cat.name.toLowerCase() === formState.category.toLowerCase()
    );
  }

  if (formState.searchTerm) {
    packageCats = packageCats.map(category => ({
      ...category,
      items: category.items.filter(item => 
        item.name.toLowerCase().includes(formState.searchTerm.toLowerCase())
      )
    })).filter(cat => cat.items.length > 0);
  }

  // Group by unique heading names
  const uniqueHeadings = [...new Set(packageCats.map(cat => cat.name))];
  
  return uniqueHeadings.map(heading => {
    // Get all items under this heading
    const items = packageCats
      .filter(cat => cat.name === heading)
      .flatMap(cat => cat.items || []);
      
    return {
      heading,
      items
    };
  });
};

// Add this function to handle selecting all packages under a heading
const handleSelectAllPackages = (heading, items) => {
  // Add all items in this group to the selected services
  items.forEach(item => {
    const newItem = {
      name: heading,
      item: {
        name: item.name,
        price: item.price,
        quantity: 1,
        discount: 0,
        discountType: 'percentage', // Default to percentage discount
        tax: 0,
        charges: item.price
      }
    };
    
    // Check if this item is already selected
    const exists = formState.selectedServices.some(
      service => service.item.name === newItem.item.name && service.name === newItem.name
    );
    
    if (!exists) {
      setFormState(prev => ({
        ...prev,
        selectedServices: [...prev.selectedServices, newItem]
      }));
    }
  });
};


  // Calculation Functions
  const calculateTotalAmount = () => {
    const serviceTotal = formState.selectedServices.reduce(
      (sum, service) => sum + (service.item.price * (service.item.quantity || 1)), 0
    );
    const packageTotal = formState.selectedPackages.reduce(
      (sum, pkg) => sum + (pkg.item.price * (pkg.item.quantity || 1)), 0
    );
    return serviceTotal + packageTotal;
  };

// Updated calculateNetAmount function for EditInvoice.js
// Updated calculateNetAmount function for EditInvoice.js
const calculateNetAmount = () => {
  // First calculate the subtotal after item-specific discounts
  let subtotal = 0;
  
  // Process service items with item-specific discounts first
  formState.selectedServices.forEach(service => {
    const price = service.item.price || 0;
    const quantity = service.item.quantity || 1;
    const baseTotal = price * quantity;
    let itemTotal = baseTotal;
    
    // Apply item-specific discount
    if (service.item.discount) {
      const discountType = service.item.discountType || 'percentage';
      
      if (discountType === 'percentage') {
        const discountAmount = (baseTotal * service.item.discount) / 100;
        itemTotal = baseTotal - discountAmount;
      } else {
        // Fixed amount discount
        itemTotal = baseTotal - Math.min(service.item.discount, baseTotal);
      }
    }
    
    // Add tax if applicable (on the discounted amount)
    const taxRate = parseFloat(service.item.tax) || 0;
    if (taxRate > 0) {
      itemTotal += (itemTotal * taxRate) / 100;
    }
    
    subtotal += itemTotal;
  });
  
  // Apply global discount to the subtotal
  let total = subtotal;
  
  if (formState.showDiscount) {
    if (formState.discountType === 'percentage' && formState.discountPercent) {
      const globalDiscountAmount = (subtotal * parseFloat(formState.discountPercent)) / 100;
      total = subtotal - globalDiscountAmount;
    } else if (formState.discountType === 'fixed' && formState.discountAmount) {
      total = subtotal - parseFloat(formState.discountAmount);
    }
  }
  
  // Ensure total doesn't go negative
  if (total < 0) total = 0;
  
  return total;
};

  // Payment Method Handlers
  const updatePaymentMethod = (index, field, value) => {
    setFormState(prev => {
      const updatedPaymentMethods = [...prev.paymentMethods];
      updatedPaymentMethods[index] = {
        ...updatedPaymentMethods[index],
        [field]: value
      };
      return { ...prev, paymentMethods: updatedPaymentMethods };
    });
  };

  // Add Payment Method Handler
  const addPaymentMethod = () => {
    setFormState(prev => ({
      ...prev,
      paymentMethods: [
        ...prev.paymentMethods, 
        { method: '', amount: '', note: '', date: '' }
      ]
    }));
  };

  // Remove Payment Method Handler
  const removePaymentMethod = (index) => {
    setFormState(prev => {
      // Get the payment method that's being removed
      const removedPayment = prev.paymentMethods[index];
      
      // If the payment method has an ID, add it to removedPayments for deactivation
      if (removedPayment && removedPayment.paymentId) {
        setRemovedPayments(prevRemovedPayments => [...prevRemovedPayments, removedPayment.paymentId]);
        console.log(`Payment with ID ${removedPayment.paymentId} marked for deactivation`);
      }
      
      // If it's the only payment method, just reset it instead of removing
      if (prev.paymentMethods.length === 1) {
        const updatedPaymentMethods = [...prev.paymentMethods];
        updatedPaymentMethods[0] = { method: '', amount: '', note: '', date: '' };
        return { ...prev, paymentMethods: updatedPaymentMethods };
      }
      
      return {
        ...prev,
        paymentMethods: prev.paymentMethods.filter((_, i) => i !== index)
      };
    });
  };

 // Function to format dates from YYYY-MM-DD to DD/MM/YYYY
  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Function to format dates for API in ISO format
const formatDateForAPI = (dateString) => {
  try {
    if (!dateString) return '';
    
    // If it's already an ISO string, return it
    if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
      return dateString;
    }
    
    // If it's in DD/MM/YYYY format, convert to Date object first
    if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      const [day, month, year] = dateString.split('/');
      dateString = `${year}-${month}-${day}T00:00:00.000Z`;
    }
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      // Handle invalid date
      console.warn('Invalid date encountered:', dateString);
      return ''; 
    }
    
    // Format as ISO string (yyyy-MM-dd'T'HH:mm:ss.SSS'Z')
    return date.toISOString();
  } catch (error) {
    console.error('Error formatting date for API:', error);
    // Return a fallback empty string in case of error
    return '';
  }
};

const saveInvoice = async (status) => {
  // Reset validation states
  setShowPaymentValidation(status === 'paid');
  
  // Reset all errors
  const newErrors = {
    invoiceDate: '',
    patientName: '',
    primaryConsultant: '',
    department: '',
    visitDate: '',
    admission_Date: ''
  };
  
  let isValid = true;
  
  // Validation for Save As Draft (only core details are mandatory)
  if (status === 'draft') {
    if (!formState.invoiceDate) {
      newErrors.invoiceDate = 'Please fill up Invoice Date';
      isValid = false;
    }
    
    if (!formState.patientName) {
      newErrors.patientName = 'Please fill up Patient Name/ID';
      isValid = false;
    }
    
    if (!formState.primaryConsultant) {
      newErrors.primaryConsultant = 'Please fill up Primary Consultant';
      isValid = false;
    }
    
    if (!formState.department) {
      newErrors.department = 'Please fill up Department';
      isValid = false;
    }
    
    // Department-specific date validations
    if (formState.department === 'OPD' && !formState.visitDate) {
      newErrors.visitDate = 'Please fill up Visit Date';
      isValid = false;
    }
    
    if (formState.department === 'IPD' && !formState.admission_Date) {
      newErrors.admission_Date = 'Please fill up Admission Date';
      isValid = false;
    }
    
    // Update the errors state
    setErrors(newErrors);
    
    if (!isValid) {
      // Don't proceed if validation fails
      return;
    }
  } 
  // Validation for Save As Paid (full validation)
  else if (status === 'paid') {
    // Check if there are any selected services or packages
    if (formState.selectedServices.length === 0 && formState.selectedPackages.length === 0) {
      swal({
        title: "Error!",
        text: "Please add at least one service or package to the invoice",
        icon: "error"
      });
      return;
    }
    
    // Check if discount is applied but no reason is selected
    if (formState.showDiscount && !formState.discountReason) {
      swal({
        title: "Error!",
        text: "Please select a discount reason",
        icon: "error"
      });
      return;
    }

    // Check if at least one payment method is added with value
    const validPayments = formState.paymentMethods.filter(payment => 
      payment.method && payment.amount && parseFloat(payment.amount) > 0
    );
    
    if (validPayments.length === 0) {
      swal({
        title: "Error!",
        text: "Please add at least one payment method with amount",
        icon: "error"
      });
      return;
    }
  }
  
  // Ensure patientId is a number
  const patientId = formState.patientId ? 
    (isNaN(formState.patientId) ? 1001 : parseInt(formState.patientId)) : 
    1001;
  
  // Get doctorId from localStorage
  const doctorId = localStorage.getItem("userId") || ""; 

  try {
    setFormState(prev => ({ ...prev, loading: true }));
    
    // Calculate the total amount including both services and packages
    const totalAmount = calculateTotalAmount();
    const netAmount = calculateNetAmount();
    
    // Calculate the total item discounts
    const totalItemDiscountAmount = calculateTotalItemDiscounts();
    
    // First, handle deactivation of removed items if any
    if (removedItems.length > 0 && status === 'draft') {
      console.log('Deactivating removed items:', removedItems);
      
      // Deactivate each removed item
      for (const itemId of removedItems) {
        try {
          const deactivateResponse = await fetch(`${C.getUrl()}/invoice/item/deactivate/${itemId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (!deactivateResponse.ok) {
            console.error(`Failed to deactivate item ${itemId}`);
          } else {
            console.log(`Successfully deactivated item ${itemId}`);
          }
        } catch (error) {
          console.error(`Error deactivating item ${itemId}:`, error);
        }
      }
    }
    
    // Handle deactivation of removed payment methods if any
    if (removedPayments.length > 0 && status === 'draft') {
      console.log('Deactivating removed payment methods:', removedPayments);
      
      // Deactivate each removed payment method
      for (const paymentId of removedPayments) {
        try {
          const deactivateResponse = await fetch(`${C.getUrl()}/invoice/payment/deactivate/${paymentId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          
          if (!deactivateResponse.ok) {
            console.error(`Failed to deactivate payment method ${paymentId}`);
          } else {
            console.log(`Successfully deactivated payment method ${paymentId}`);
          }
        } catch (error) {
          console.error(`Error deactivating payment method ${paymentId}:`, error);
        }
      }
    }
    
    // Clear the deactivation arrays after processing
    if (status === 'draft') {
      setRemovedItems([]);
      setRemovedPayments([]);
    }
    
    // 1. Update main invoice record with current timestamp for update time
    const currentDateTime = new Date().toISOString();
    
    // UPDATED: Set global discount fields correctly based on discount type
    // Initialize all discount-related fields with appropriate default values
    let dpercentag = 0;  // lowercase 'd' to match API expectations
    let discountValue = 0;
    let invoicePType = "";
    let invoiceR = "";
    let invoiceRType = "";
    
    if (formState.showDiscount) {
      if (formState.discountType === 'percentage') {
        // Set percentage discount fields
        dpercentag = parseFloat(formState.discountPercent) || 0;
        invoicePType = "Percentage(%)";
        // Reset fixed amount fields to ensure they don't override
        discountValue = 0;
        invoiceR = "";
        invoiceRType = "";
      } else {
        // Set fixed amount discount fields
        discountValue = parseFloat(formState.discountAmount) || 0;
        invoiceR = discountValue.toString();
        invoiceRType = "Fixed amount";
        // Reset percentage fields to ensure they don't override
        dpercentag = 0;
        invoicePType = "";
      }
    } else {
      // If discount is disabled, reset all discount fields
      dpercentag = 0;
      discountValue = 0;
      invoicePType = "";
      invoiceR = "";
      invoiceRType = "";
    }
    
    const invoiceUpdatePayload = {
      patientName: formState.patientName,
      updateDate: currentDateTime,
      updateTime: currentDateTime,
      patientId: patientId,
      
      // UPDATED: Set both lowercase and uppercase versions of discount percentage
      // to ensure compatibility with different API endpoints
      dpercentag: dpercentag,  // lowercase - matches AddInvoice.js
      Dpercentag: dpercentag,  // uppercase - was used in the original EditInvoice.js
      
      discount: discountValue,
      invoicePType: invoicePType,
      invoiceR: invoiceR,
      invoiceRType: invoiceRType,
      
      // Add the total item discounts field
      invoiceTotalDis: totalItemDiscountAmount.toFixed(2),
      
      invoiceDate: formatDateForAPI(formState.invoiceDate),
      // Use appropriate dates based on department
      visitDate: formState.department === 'OPD' ? formatDateForAPI(formState.visitDate) : null,
      admission_Date: formState.department === 'IPD' ? formatDateForAPI(formState.admission_Date) : null,
      discharge_Date: formState.department === 'IPD' ? formatDateForAPI(formState.discharge_Date) : null,
      department: formState.department,
      status: status === 'paid' ? 'COMPLETED' : 'pending',
      netAmount: Number(netAmount),
      disResone: formState.showDiscount ? formState.discountReason : '',
      received: status === 'paid' ? 
        Number(formState.paymentMethods.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0)) : 0,
      primaryConsultant: formState.primaryConsultant,
      comments: formState.comments || '',
      updatedBy: doctorId,
      serviceStatus: status === 'paid' ? "SUCCESS" : "DRAFT"
    };
    
    // Log the payload for debugging
    console.log('Updating invoice:', JSON.stringify(invoiceUpdatePayload, null, 2));
    
    // Update the main invoice
    const invoiceResponse = await fetch(`${C.getUrl()}/invoice/updateByInvoiceNumber/${invoiceNumber}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(invoiceUpdatePayload)
    });
    
    if (!invoiceResponse.ok) {
      const errorText = await invoiceResponse.text();
      console.error('Invoice update error:', errorText);
      throw new Error(`Failed to update invoice: ${errorText}`);
    }
    
    // 2. Update items - only if there are items to update
    if (formState.selectedServices.length > 0 || formState.selectedPackages.length > 0) {
      const allItems = [
        ...formState.selectedServices.map((service) => {
          // UPDATED: Handle individual item discount using the correct field names
          let itemP = 0;
          let itemPType = "";
          let itemR = "0"; // Default to "0" for fixed amount (matches the example response)
          let itemRType = "";
          
          // Set discount based on type
          if (service.item.discountType === 'percentage') {
            itemP = parseFloat(service.item.discount) || 0;
            itemPType = "%";
            // Reset fixed amount fields
            itemR = "0";
            itemRType = "";
          } else {
            // Fixed amount discount
            itemR = (parseFloat(service.item.discount) || 0).toString();
            itemRType = "₹";
            // Reset percentage fields
            itemP = 0;
            itemPType = "";
          }
          
          // Standard discount field is always set regardless of type
          const discountValue = parseFloat(service.item.discount) || 0;
          
          // Calculate the correct item charges (ONLY with item discount, not global)
          const itemPrice = Number(service.item.price);
          const itemQuantity = Number(service.item.quantity || 1);
          const baseTotal = itemPrice * itemQuantity;
          
          // Calculate item-specific discount
          let itemDiscountAmount = 0;
          if (discountValue) {
            if (service.item.discountType === 'percentage') {
              itemDiscountAmount = (baseTotal * discountValue) / 100;
            } else {
              itemDiscountAmount = Math.min(discountValue, baseTotal);
            }
          }
          
          // Calculate the item's net amount (without global discount)
          const itemNetAmount = baseTotal - itemDiscountAmount;
          
          return {
            id: service.itemId, // Include ID for existing items
            costPerUnit: itemPrice,
            quantity: itemQuantity,
            discount: discountValue,
            // UPDATED: Set item discount fields using the correct field names
            itemP: itemP,
            itemPType: itemPType,
            itemR: itemR,
            itemRType: itemRType,
            // Store the item's total discount amount in rupees
            itemTotalDis: itemDiscountAmount.toFixed(2),
            tax: Number(service.item.tax || 0),
            charges: Number(service.item.charges || 0),
            category: service.name,
            item: service.item.name,
            netAmount: itemNetAmount, // Store the individual item's net amount
            patientId: patientId,
            updatedBy: doctorId,
            activeInd: "Y",
            createdDate: formatDateForAPI(formState.invoiceDate)
          };
        }),
        ...formState.selectedPackages.map((pkg) => {
          // Handle package discounts the same way as service items
          let itemP = 0;
          let itemPType = "";
          let itemR = "0"; // Default to "0" for fixed amount (matches the example response)
          let itemRType = "";
          
          if (pkg.item.discountType === 'percentage') {
            itemP = parseFloat(pkg.item.discount) || 0;
            itemPType = "%";
            // Reset fixed amount fields
            itemR = "0";
            itemRType = "";
          } else {
            // Fixed amount discount
            itemR = (parseFloat(pkg.item.discount) || 0).toString();
            itemRType = "₹";
            // Reset percentage fields
            itemP = 0;
            itemPType = "";
          }
          
          const discountValue = parseFloat(pkg.item.discount) || 0;
          
          // Calculate item-specific discount amount
          const itemPrice = Number(pkg.item.price);
          const itemQuantity = Number(pkg.item.quantity || 1);
          const baseTotal = itemPrice * itemQuantity;
          
          let itemDiscountAmount = 0;
          if (discountValue) {
            if (pkg.item.discountType === 'percentage') {
              itemDiscountAmount = (baseTotal * discountValue) / 100;
            } else {
              itemDiscountAmount = Math.min(discountValue, baseTotal);
            }
          }
          
          return {
            id: pkg.itemId, // Include ID for existing items
            costPerUnit: itemPrice,
            quantity: itemQuantity,
            discount: discountValue,
            itemP: itemP,
            itemPType: itemPType,
            itemR: itemR,
            itemRType: itemRType,
            // Store the item's total discount amount in rupees
            itemTotalDis: itemDiscountAmount.toFixed(2),
            tax: Number(pkg.item.tax || 0),
            charges: Number(pkg.item.charges || 0),
            category: pkg.name,
            item: pkg.item.name,
            netAmount: Number(pkg.item.charges || (pkg.item.price * (pkg.item.quantity || 1))),
            patientId: patientId,
            updatedBy: doctorId,
            activeInd: "Y",
            createdDate: formatDateForAPI(formState.invoiceDate)
          };
        })
      ];
      
      const itemsPayload = {
        items: allItems,
        invoice: {
          invoiceNumber: invoiceNumber
        }
      };
      
      console.log('Updating items:', JSON.stringify(itemsPayload, null, 2));
      
      const itemsResponse = await fetch(`${C.getUrl()}/invoice/itemUpdate/${invoiceNumber}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(itemsPayload)
      });
      
      if (!itemsResponse.ok) {
        const errorText = await itemsResponse.text();
        console.error('Items update error:', errorText);
        throw new Error(`Failed to update invoice items: ${errorText}`);
      }
    }
    
    // 3. Update payments - only if it's a paid invoice or if there are valid payment methods
    const validPayments = formState.paymentMethods.filter(payment => 
      payment.method && payment.amount && parseFloat(payment.amount) > 0
    );
    
    if (status === 'paid' || validPayments.length > 0) {
      const paymentsPayload = {
        payments: validPayments.map(payment => ({
          ...(payment.paymentId && { id: payment.paymentId }),
          paymentMethod: payment.method,
          paymentAmount: Number(parseFloat(payment.amount || 0)),
          otherNote: payment.note || '',
          paymentDate: payment.date 
            ? formatDateForAPI(payment.date) 
            : formatDateForAPI(formState.invoiceDate),
          patientId: patientId,
          updatedBy: doctorId,
          createdDate: formatDateForAPI(formState.invoiceDate)
        })),
        invoice: {
          invoiceNumber: invoiceNumber  
        }
      };
      
      // Only make the API call if there are payments to update
      if (paymentsPayload.payments.length > 0) {
        console.log('Updating payments:', JSON.stringify(paymentsPayload, null, 2));
        
        const paymentsResponse = await fetch(`${C.getUrl()}/invoice/updatePayments/${invoiceNumber}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(paymentsPayload)
        });
        
        if (!paymentsResponse.ok) {
          const errorText = await paymentsResponse.text();
          console.error('Payments update error:', errorText);
          throw new Error(`Failed to update invoice payments: ${errorText}`);
        }
      } else if (status === 'paid') {
        // If it's a paid invoice but no payment methods, we should throw an error
        throw new Error('At least one payment method with amount is required for paid invoices');
      }
      // For draft with no payments, we don't need to call the payment update endpoint
    }
    
    setFormState(prev => ({ ...prev, loading: false }));
    
    swal({
      title: status === 'draft' ? "Draft Updated!" : "Success!",
      text: status === 'draft' 
        ? "Invoice updated as draft successfully" 
        : "Invoice updated successfully",
      icon: "success"
    });
    
    // Redirect to invoices list
    history.push('/invoices');
  } catch (error) {
    console.error('Error updating invoice:', error);
    setFormState(prev => ({ ...prev, loading: false }));
    
    swal({
      title: "Error!",
      text: error.message || 'An error occurred while updating the invoice',
      icon: "error"
    });
  }
};


const calculateTotalItemDiscounts = () => {
  let totalItemDiscountAmount = 0;
  
  // Sum all item-specific discounts and convert to amount
  formState.selectedServices.forEach(service => {
    const price = service.item.price || 0;
    const quantity = service.item.quantity || 1;
    const baseTotal = price * quantity;
    const discountType = service.item.discountType || 'percentage';
    const discount = service.item.discount || 0;
    
    // Calculate discount amount based on type
    let discountAmount = 0;
    if (discountType === 'percentage') {
      discountAmount = (baseTotal * discount) / 100;
    } else {
      // Fixed amount
      discountAmount = Math.min(discount, baseTotal);
    }
    
    totalItemDiscountAmount += discountAmount;
  });
  
  return totalItemDiscountAmount;
};


const handleItemChange = (index, field, value) => {
  setFormState(prev => {
    const updatedServices = [...prev.selectedServices];
    
    // For nested item fields
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      updatedServices[index][parent] = {
        ...updatedServices[index][parent],
        [child]: value
      };
      
      // Ensure discountType is set to 'percentage' if not already defined
      if (child === 'discount' && !updatedServices[index].item.discountType) {
        updatedServices[index].item.discountType = 'percentage';
      }
    } else {
      // For top-level fields
      updatedServices[index][field] = value;
    }
    
    // Recalculate if quantity, price, or discount changes
    if (
      field === 'item.quantity' || 
      field === 'item.price' || 
      field === 'item.discount'
    ) {
      // Important: When recalculating, only apply the item's own discount, not global
      recalculateItemCharges(
        updatedServices, 
        index, 
        false, // Don't apply global discount here
        0      // No global discount percentage
      );
    }
    
    return {
      ...prev,
      selectedServices: updatedServices
    };
  });
};

  // Item Selection Handlers
  const handleServiceSelect = (category, item) => {
    const newItem = {
      name: category.name,
      item: {
        name: item.name,
        price: item.price,
        quantity: 1,
        discount: 0,
        discountType: 'percentage', // Default to percentage discount type
        tax: 0,
        charges: item.price
      }
    };
  
    setFormState(prev => {
      // Always update selectedServices regardless of active tab
      const currentItems = prev.selectedServices;
      const exists = currentItems.some(
        i => i.item.name === newItem.item.name && i.name === newItem.name
      );
  
      return {
        ...prev,
        selectedServices: exists 
          ? currentItems 
          : [...currentItems, newItem]
      };
    });
  };

  const handlePackageChange = (index, field, value) => {
    setFormState(prev => {
      const updatedPackages = [...prev.selectedPackages];
      
      // For nested item fields
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        updatedPackages[index][parent] = {
          ...updatedPackages[index][parent],
          [child]: value
        };
      } else {
        // For top-level fields
        updatedPackages[index][field] = value;
      }
      
      // Recalculate charges based on quantity and price
      if (field === 'item.quantity' || field === 'item.price') {
        const price = updatedPackages[index].item.price;
        const quantity = updatedPackages[index].item.quantity;
        const discPercent = prev.showDiscount ? prev.discountPercent : 0;
        const itemDiscount = (price * quantity * discPercent) / 100;
        
        updatedPackages[index].item.charges = (price * quantity) - itemDiscount;
      }
      
      return {
        ...prev,
        selectedPackages: updatedPackages
      };
    });
  };

  // Add a function to remove a package
const removePackage = (index) => {
  setFormState(prev => {
    // Get the package that's being removed
    const removedPackage = prev.selectedPackages[index];
    
    // If the package has an ID, add it to removedItems for deactivation
    if (removedPackage && removedPackage.itemId) {
      setRemovedItems(prevRemovedItems => [...prevRemovedItems, removedPackage.itemId]);
      console.log(`Package with ID ${removedPackage.itemId} marked for deactivation`);
    }
    
    return {
      ...prev,
      selectedPackages: prev.selectedPackages.filter((_, i) => i !== index)
    };
  });
};

  // Item Removal Handlers
  const removeItem = (index) => {
    setFormState(prev => {
      // Get the item that's being removed
      const removedItem = prev.selectedServices[index];
      
      // If the item has an ID, add it to removedItems for deactivation
      if (removedItem && removedItem.itemId) {
        setRemovedItems(prevRemovedItems => [...prevRemovedItems, removedItem.itemId]);
        console.log(`Item with ID ${removedItem.itemId} marked for deactivation`);
      }
      
      return {
        ...prev,
        selectedServices: prev.selectedServices.filter((_, i) => i !== index)
      };
    });
  };

  const clearAllItems = () => {
    if (formState.activeTab === 'services') {
      setFormState(prev => {
        // Add all service items with IDs to the removedItems list for deactivation
        const itemsToDeactivate = prev.selectedServices
          .filter(item => item.itemId)
          .map(item => item.itemId);
        
        if (itemsToDeactivate.length > 0) {
          setRemovedItems(prevRemovedItems => [...prevRemovedItems, ...itemsToDeactivate]);
          console.log(`Marked ${itemsToDeactivate.length} service items for deactivation`);
        }
        
        return {
          ...prev,
          selectedServices: []
        };
      });
    } else {
      setFormState(prev => {
        // Add all package items with IDs to the removedItems list for deactivation
        const itemsToDeactivate = prev.selectedPackages
          .filter(item => item.itemId)
          .map(item => item.itemId);
        
        if (itemsToDeactivate.length > 0) {
          setRemovedItems(prevRemovedItems => [...prevRemovedItems, ...itemsToDeactivate]);
          console.log(`Marked ${itemsToDeactivate.length} package items for deactivation`);
        }
        
        return {
          ...prev,
          selectedPackages: []
        };
      });
    }
  };

  // Function to clear all payment methods
  const clearAllPayments = () => {
    setFormState(prev => {
      // Add all payment methods with IDs to the removedPayments list for deactivation
      const paymentsToDeactivate = prev.paymentMethods
        .filter(payment => payment.paymentId)
        .map(payment => payment.paymentId);
      
      if (paymentsToDeactivate.length > 0) {
        setRemovedPayments(prevRemovedPayments => [...prevRemovedPayments, ...paymentsToDeactivate]);
        console.log(`Marked ${paymentsToDeactivate.length} payment methods for deactivation`);
      }
      
      // Reset to a single empty payment method
      return {
        ...prev,
        paymentMethods: [{ method: '', amount: '', note: '', date: '' }]
      };
    });
  };

  const toggleDiscount = () => {
  setFormState(prev => {
    // If we're turning discount off, reset all discount-related values
    if (prev.showDiscount) {
      return {
        ...prev,
        showDiscount: false,
        discountPercent: '', // Use empty string instead of '0' to ensure it's treated as no discount
        discountAmount: '',
        discountReason: ''
      };
    } 
    // Otherwise just turn discount on
    return {
      ...prev,
      showDiscount: true,
      discountPercent: '', // Initialize with empty string instead of '0'
      discountAmount: ''
    };
  });
};

  // Rendering Logic
  // const renderServiceCategories = () => {
  //   const categories = formState.activeTab === 'services' 
  //     ? serviceCategories 
  //     : packageCategories;
  
  //   // Apply filters
  //   let filteredCategories = categories || [];
    
  //   if (formState.category) {
  //     filteredCategories = filteredCategories.filter(
  //       cat => cat.name.toLowerCase() === formState.category.toLowerCase()
  //     );
  //   }
  
  //   if (formState.searchTerm) {
  //     filteredCategories = filteredCategories.map(category => ({
  //       ...category,
  //       items: category.items.filter(item => 
  //         item.name.toLowerCase().includes(formState.searchTerm.toLowerCase())
  //       )
  //     })).filter(cat => cat.items.length > 0);
  //   }
  
  //   return filteredCategories;
  // };
  const renderServiceCategories = () => {
    const categories = formState.activeTab === 'services' 
      ? serviceCategories 
      : packageCategories;
  
    // Apply filters
    let filteredCategories = [...(categories || [])];
    
    // Apply category filter
    if (formState.category) {
      filteredCategories = filteredCategories.filter(
        cat => cat.name.toLowerCase() === formState.category.toLowerCase()
      );
    }
  
    // Apply vendor filter
    if (formState.vendor && formState.vendor.trim() !== '') {
      filteredCategories = filteredCategories.map(category => {
        return {
          ...category,
          items: (category.items || []).filter(item => {
            // Check if the item has a vendor property and it matches the selected vendor
            if (!item.vendor) return false;
            
            // Case-insensitive comparison with trimming
            return item.vendor.trim().toLowerCase() === formState.vendor.trim().toLowerCase();
          })
        };
      }).filter(cat => cat.items && cat.items.length > 0); // Only keep categories with matching items
    }
  
    // Apply search term filter
    if (formState.searchTerm) {
      filteredCategories = filteredCategories.map(category => ({
        ...category,
        items: (category.items || []).filter(item => 
          item.name.toLowerCase().includes(formState.searchTerm.toLowerCase())
        )
      })).filter(cat => cat.items && cat.items.length > 0);
    }
  
    return filteredCategories;
  };

  // Render loading state
  if (formState.loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner animation="border" role="status">
          <span className="sr-only">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <div className="d-flex" id="wrapper">
      <Side />
      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <Top />
          <Container fluid>
            {formState.loading && <Loader />}
            
            {formState.error && (
              <div className="alert alert-danger m-3">
                {formState.error}
              </div>
            )}
            
            <div className="p-4">
              <Row>
                {/* Left column - Form fields and tables */}
                <Col md={8}>
                  {/* Invoice Details Section */}
                  <Row>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Invoice Date<span className="text-danger">*</span></Form.Label>
                        <Form.Control 
                          type="date"
                          name="invoiceDate"
                          value={formState.invoiceDate} 
                          onChange={handleChange}
                          min={getMinDate()}
                          isInvalid={!!errors.invoiceDate}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.invoiceDate}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                    <Form.Group>
                      <Form.Label>Patient Name/ID <span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        type="text"
                        name="patientName"
                        value={formState.patientName}
                        onChange={handleChange}
                        placeholder="Enter Patient Name/ID"
                        isInvalid={!!errors.patientName}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.patientName}
                      </Form.Control.Feedback>
                      {formState.patientId && (
                        <small className="text-muted">Patient ID: {formState.patientId}</small>
                      )}
                    </Form.Group>
                    </Col>
                  </Row>
  
                  {/* Consultant and Department */}
                  <Row className="mt-3">
                    <Col md={6}>
                    <Form.Group>
                      <Form.Label>Primary Consultant<span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        as="select"
                        name="primaryConsultant"
                        value={formState.primaryConsultant}
                        onChange={handleChange}
                        isInvalid={!!errors.primaryConsultant}
                      >
                        <option value="">Select Consultant</option>
                        {consultants.map((consultant, index) => (
                          <option key={index} value={consultant}>{consultant}</option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.primaryConsultant}
                      </Form.Control.Feedback>
                    </Form.Group>
                    </Col>
                    <Col md={6}>
                    <Form.Group>
                      <Form.Label>Department<span className="text-danger">*</span></Form.Label>
                      <Form.Control 
                        as="select"
                        name="department"
                        value={formState.department}
                        onChange={handleChange}
                        isInvalid={!!errors.department}
                      >
                        <option value="">Select Department</option>
                        {departments.map((dept, index) => (
                          <option key={index} value={dept}>{dept}</option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.department}
                      </Form.Control.Feedback>
                    </Form.Group>
                    </Col>
                  </Row>
                  
                  {/* Visit Date Field */}
                  <Row className="mt-3">
  {formState.department === 'OPD' && (
    <Col md={6}>
      <Form.Group>
        <Form.Label>Visit Date<span className="text-danger">*</span></Form.Label>
        <Form.Control 
          type="date"
          name="visitDate"
          value={formState.visitDate} 
          onChange={handleChange}
          min={getMinDate()}
          isInvalid={!!errors.visitDate}
        />
        <Form.Control.Feedback type="invalid">
          {errors.visitDate}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  )}
  
  {formState.department === 'IPD' && (
    <>
      <Col md={6}>
        <Form.Group>
          <Form.Label>Admission Date<span className="text-danger">*</span></Form.Label>
          <Form.Control 
            type="date"
            name="admission_Date"
            value={formState.admission_Date || ''} 
            onChange={handleChange}
            min={getMinDate()}
            isInvalid={!!errors.admission_Date}
          />
          <Form.Control.Feedback type="invalid">
            {errors.admission_Date}
          </Form.Control.Feedback>
        </Form.Group>
      </Col>
      <Col md={6}>
        <Form.Group>
          <Form.Label>Discharge Date</Form.Label>
          <Form.Control 
            type="date"
            name="discharge_Date"
            value={formState.discharge_Date || ''} 
            onChange={handleChange}
            min={formState.admission_Date || getMinDate()}
          />
        </Form.Group>
      </Col>
    </>
  )}
  
  {formState.department !== 'OPD' && formState.department !== 'IPD' && formState.department && (
    <Col md={6}>
      <Form.Group>
        <Form.Label>Visit Date<span className="text-danger">*</span></Form.Label>
        <Form.Control 
          type="date"
          name="visitDate"
          value={formState.visitDate} 
          onChange={handleChange}
          min={getMinDate()}
          isInvalid={!!errors.visitDate}
        />
        <Form.Control.Feedback type="invalid">
          {errors.visitDate}
        </Form.Control.Feedback>
      </Form.Group>
    </Col>
  )}
</Row>

                  {/* Items Table */}
                  <Row className="mt-3">
                    <Col>
                      <Table striped bordered hover responsive>
                        <thead className="table-header-font">
                          <tr>
                            <th>ITEM</th>
                            <th>COST PER UNIT (₹)</th>
                            <th>QTY</th>
                            <th>DISCOUNT</th>
                            <th>TAX (%)</th>
                            <th>CHARGES (₹)</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formState.selectedServices.length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center text-danger">
                                <div>No Items Added to this list</div>
                                <small>Please add at least one item from the right panel</small>
                              </td>
                            </tr>
                          )}
                           {formState.selectedServices.map((service, index) => {
                            // Calculate the correct display price that includes ONLY item-specific discount
                            const price = service.item.price || 0;
                            const quantity = service.item.quantity || 1;
                            const baseTotal = price * quantity;
                            
                            // Calculate item-specific discount
                            let itemDiscountAmount = 0;
                            if (service.item.discount) {
                              if (service.item.discountType === 'percentage') {
                                itemDiscountAmount = (baseTotal * service.item.discount) / 100;
                              } else {
                                // Fixed amount discount
                                itemDiscountAmount = Math.min(service.item.discount, baseTotal);
                              }
                            }
                            
                            // Calculate display price after item-specific discount ONLY (no global discount here)
                            const displayPrice = baseTotal - itemDiscountAmount;
                            
                            // Calculate tax if applicable
                            const taxRate = parseFloat(service.item.tax) || 0;
                            const taxAmount = displayPrice * (taxRate / 100);
                            const priceWithTax = displayPrice + taxAmount;
                            
                            return (
                              <tr key={index}>
                                {/* <td>{service.item.name}</td> */}
                                <td>
      <div>{service.item.name}</div>
      <div className="small text-muted">
        Category: {service.category || service.name}
        {service.vendor && service.vendor.trim() !== '' ? ` | Vendor: ${service.vendor}` : ''}
      </div>
    </td>
                                <td>
                                  <Form.Control 
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={price}
                                    onChange={(e) => handleItemChange(index, 'item.price', parseFloat(e.target.value) || 0)}
                                    style={{ width: '100px' }}
                                  />
                                </td>
                                <td>
                                  <Form.Control 
                                    type="number"
                                    min="1"
                                    value={quantity}
                                    onChange={(e) => handleItemChange(index, 'item.quantity', parseInt(e.target.value) || 1)}
                                    style={{ width: '70px' }}
                                  />
                                </td>
                                <td>
                                  <InputGroup className="mb-0">
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      value={service.item.discount || 0}
                                      onChange={(e) => handleItemChange(index, 'item.discount', parseFloat(e.target.value) || 0)}
                                      style={{ width: '70px' }}
                                    />
                                    <InputGroup.Append>
                                      <Form.Control
                                        as="select"
                                        value={service.item.discountType || 'percentage'}
                                        onChange={(e) => handleItemDiscountTypeChange(index, e.target.value)}
                                        style={{ width: '60px' }}
                                      >
                                        <option value="percentage">%</option>
                                        <option value="fixed">₹</option>
                                      </Form.Control>
                                    </InputGroup.Append>
                                  </InputGroup>
                                </td>
                                <td>
                                  <Form.Control 
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={service.item.tax || 0}
                                    onChange={(e) => handleItemChange(index, 'item.tax', parseFloat(e.target.value) || 0)}
                                    style={{ width: '70px' }}
                                  />
                                </td>
                                <td>₹{priceWithTax.toFixed(2)}</td>
                                <td>
                                  <Button 
                                    variant="danger" 
                                    size="sm"
                                    onClick={() => removeItem(index)}
                                  >
                                    Remove
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        {formState.selectedServices.length > 0 && (
                          <tfoot>
                            <tr>
                              <td colSpan="5" className="text-right font-weight-bold">
                                Net Amount
                              </td>
                              <td className="font-weight-bold">₹{calculateNetAmount().toFixed(2)}</td>
                              <td></td>
                            </tr>
                          </tfoot>
                        )}
                      </Table>
 
               {formState.showDiscount ? (
                <div className="border p-3 mb-3">
                  <Row>
                    <Col md={2}>
                      <Button 
                        variant="link" 
                        className="text-danger p-0" 
                        onClick={toggleDiscount}
                      >
                        Remove Discount
                      </Button>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label>Discount Type</Form.Label>
                        <Form.Control
                          as="select"
                          value={formState.discountType}
                          onChange={(e) => handleDiscountTypeChange(e.target.value)}
                        >
                          <option value="percentage">Percentage (%)</option>
                          <option value="fixed">Fixed Amount (₹)</option>
                        </Form.Control>
                      </Form.Group>
                    </Col>
                    {formState.discountType === 'percentage' ? (
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Discount Percentage</Form.Label>
                          <InputGroup>
                            <Form.Control
                              type="number"
                              placeholder="0"
                              name="discountPercent"
                              value={formState.discountPercent}
                              onChange={handleChange}
                              min="0"
                              max="100"
                            />
                            <InputGroup.Append>
                              <InputGroup.Text>%</InputGroup.Text>
                            </InputGroup.Append>
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    ) : (
                      <Col md={3}>
                        <Form.Group>
                          <Form.Label>Discount Amount</Form.Label>
                          <InputGroup>
                            <InputGroup.Prepend>
                              <InputGroup.Text>₹</InputGroup.Text>
                            </InputGroup.Prepend>
                            <Form.Control
                              type="number"
                              placeholder="Amount"
                              name="discountAmount"
                              value={formState.discountAmount}
                              onChange={handleChange}
                              min="0"
                            />
                          </InputGroup>
                        </Form.Group>
                      </Col>
                    )}
                                        <Col md={3}>
                            <Form.Group>
                              <Form.Label>Discount Reason <span className="text-danger">*</span></Form.Label>
                              <Form.Control
                                as="select"
                                name="discountReason"
                                value={formState.discountReason}
                                onChange={handleChange}
                                className={formState.showDiscount && !formState.discountReason ? "border-danger" : ""}
                              >
                                <option value="">Select Reason</option>
                                {discountReasons.map((reason, index) => (
                                  <option key={index} value={reason}>{reason}</option>
                                ))}
                              </Form.Control>
                              {formState.showDiscount && !formState.discountReason && (
                                <small className="text-danger">Please select a discount reason</small>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div className="d-flex justify-content-between">
                        <Button variant="link" onClick={toggleDiscount}>
                          Add Discount
                        </Button>
                        {formState.selectedServices.length > 0 && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={clearAllItems}
                          >
                            Clear All Items
                          </Button>
                        )}
                      </div>
                    )}
                    </Col>
                  </Row>

                  {/* Payment Methods */}
                  <Row className="mt-3">
                    <Col>
                      <div className="d-flex justify-content-between mb-2">
                        {formState.paymentMethods.length > 0 && 
                        formState.paymentMethods.some(p => p.method || p.amount || p.note || p.date) && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={clearAllPayments}
                          >
                            Clear All Payments
                          </Button>
                        )}
                      </div>
                      
                      <Table striped bordered hover responsive>
                        <thead className="table-header-font">
                          <tr>
                            <th>#</th>
                            <th>PAYMENT METHOD <span className="text-danger">*</span></th>
                            <th>PAYMENT AMOUNT <span className="text-danger">*</span></th>
                            <th>OTHER NOTE</th>
                            <th>PAYMENT DATE</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formState.paymentMethods.map((payment, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <div>
                                  <Form.Control 
                                    as="select"
                                    value={payment.method}
                                    onChange={(e) => updatePaymentMethod(index, 'method', e.target.value)}
                                    className={showPaymentValidation && !payment.method ? "border-danger" : ""}
                                  >
                                    <option value="">Select</option>
                                    {paymentMethodOptions.map((method, idx) => (
                                      <option key={idx} value={method}>{method}</option>
                                    ))}
                                  </Form.Control>
                                  {showPaymentValidation && !payment.method && (
                                    <small className="text-danger d-block">Payment Method is Mandatory</small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <div>
                                  <Form.Control 
                                    type="number"
                                    value={payment.amount}
                                    onChange={(e) => updatePaymentMethod(index, 'amount', e.target.value)}
                                    placeholder="Amount"
                                    className={showPaymentValidation && !payment.amount ? "border-danger" : ""}
                                  />
                                  {showPaymentValidation && !payment.amount && (
                                    <small className="text-danger d-block">Payment Amount is Mandatory</small>
                                  )}
                                </div>
                              </td>
                              <td>
                                <Form.Control 
                                  type="text"
                                  value={payment.note}
                                  onChange={(e) => updatePaymentMethod(index, 'note', e.target.value)}
                                  placeholder="Notes"
                                />
                              </td>
                              <td>
                                <Form.Control 
                                  type="date"
                                  value={payment.date 
                                    ? payment.date.split('/').reverse().join('-')  // Convert DD/MM/YYYY -> YYYY-MM-DD
                                    : ''} 
                                  onChange={(e) => {
                                    const formattedDate = formatDateToDDMMYYYY(e.target.value); // Convert back to DD/MM/YYYY
                                    updatePaymentMethod(index, 'date', formattedDate);
                                  }}
                                  min={getMinDate()}
                                />
                              </td>
                              <td>
                                {index !== 0 ? (
                                  <Button 
                                    variant="danger" 
                                    size="sm"
                                    onClick={() => removePaymentMethod(index)}
                                  >
                                    Remove
                                  </Button>
                                ) : (
                                  <Button 
                                    variant="outline-secondary" 
                                    size="sm"
                                    onClick={() => {
                                      // Clear only the first row's data
                                      setFormState(prev => {
                                        const updatedPaymentMethods = [...prev.paymentMethods];
                                        updatedPaymentMethods[0] = { 
                                          method: '', 
                                          amount: '', 
                                          note: '', 
                                          date: '' 
                                        };
                                        return {
                                          ...prev,
                                          paymentMethods: updatedPaymentMethods
                                        };
                                      });
                                    }}
                                  >
                                    Clear
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                      
                      {/* Add Payment button */}
                      <div className="d-flex justify-content-start mt-2">
                        <Button 
                          variant="outline-primary"
                          onClick={addPaymentMethod}
                        >
                          Add Payment Method
                        </Button>
                      </div>
                    </Col>
                  </Row>

                  {/* Comments Section */}
                  <Row className="mt-3">
                    <Col>
                      <Form.Group>
                        <Form.Label>Comments, Notes and Special Instructions</Form.Label>
                        <Form.Control 
                          as="textarea" 
                          rows={3}
                          name="comments"
                          value={formState.comments}
                          onChange={handleChange}
                          placeholder="Enter any additional comments or instructions"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Action Buttons */}
                  <Row className="mt-3">
                    <Col className="d-flex justify-content-end">
                      <Button 
                        variant="outline-secondary" 
                        className="mr-2"
                        onClick={() => history.push('/invoices')}
                      >
                        Cancel
                      </Button>
                      <Button 
                        variant="outline-primary" 
                        className="mr-2"
                        onClick={() => saveInvoice('draft')}
                      >
                        Save As Draft
                      </Button>
                      <Button 
                        variant="primary"
                        onClick={() => saveInvoice('paid')}
                        style={{ backgroundColor: '#5ABDA3', color: 'white' }}
                      >
                        Save As Paid
                      </Button>
                    </Col>
                  </Row>
                </Col>
                
                {/* Services/Packages Selection Section */}
                <Col md={4}>
                  <Nav variant="tabs" activeKey={formState.activeTab}>
                    <Nav.Item>
                      <Nav.Link 
                        eventKey="services"
                        onClick={() => setFormState(prev => ({ ...prev, activeTab: 'services' }))}
                      >
                        By Services
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link 
                        eventKey="packages"
                        onClick={() => setFormState(prev => ({ ...prev, activeTab: 'packages' }))}
                      >
                        By Packages
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                  
                  {/* Filtering and Search Section */}
{/* Filtering and Search Section - Only for Packages tab */}
{formState.activeTab === 'packages' && (
  <Row className="my-3">
    <Col>
      <InputGroup>
        <Form.Control 
          type="text"
          placeholder="Search packages..."
          name="searchTerm"
          value={formState.searchTerm}
          onChange={handleChange}
        />
        <InputGroup.Append>
          <Button variant="outline-secondary">
            <FaSearch />
          </Button>
        </InputGroup.Append>
      </InputGroup>
    </Col>
  </Row>
)}

{/* Filtering and Search Section - Only for Services tab */}
{formState.activeTab === 'services' && (
  <>
    <Row className="mb-3">
      <Col md={6} className="pr-1">
        <Form.Control 
          as="select"
          name="category"
          value={formState.category}
          onChange={handleChange}
          className="form-control border-primary rounded"
        >
          <option value="">Select Category</option>
          {serviceCategories.map((cat, index) => (
            <option key={index} value={cat.name}>{cat.name}</option>
          ))}
        </Form.Control>
      </Col>
      <Col md={6} className="pl-1">
        <Form.Control 
          as="select"
          name="billingClass"
          value={formState.billingClass}
          onChange={handleChange}
          className="form-control"
        >
          <option value="">Select Billing Class</option>
          <option value="class1">OPD</option>
          <option value="class2">UNIPATH LAB</option>
          <option value="class3">DAY CARE</option>
          <option value="class4">ECONOMY</option>
          <option value="class5">DELUXE</option>
          <option value="class6">DAY CARE WARD</option>
        </Form.Control>
      </Col>
    </Row>

    <Row className="mb-2">
      {/* <Col md={6} className="pr-1">
        <Form.Control 
          as="select"
          name="vendor"
          value={formState.vendor}
          onChange={handleChange}
          className="form-control"
        >
          <option value="">Select Vendor</option>
          <option value="vendor1">PULSE DIAGNOSTIC</option>
          <option value="vendor2">JARIWALA LAB</option>
        </Form.Control>
      </Col> */}
      <Col md={6} className="pl-1">
              <Form.Control 
                as="select"
                name="vendor"
                value={formState.vendor}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select Vendor</option>
                {vendors.map((vendor) => (
                  <option key={vendor.id} value={vendor.venderName}>
                    {vendor.venderName}
                  </option>
                ))}
              </Form.Control>
            </Col>
      <Col md={6} className="pl-1">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search Item"
            name="searchTerm"
            value={formState.searchTerm}
            onChange={handleChange}
          />
          <InputGroup.Append>
            <Button variant="outline-secondary">
              <FaSearch />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      </Col>
    </Row>
  </>
)}

{/* Categories and Items Display */}
{/* <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
  {formState.activeTab === 'services' ? (
    // Original service rendering - don't change this
    renderServiceCategories().map((category, index) => (
      <Card key={index} className="mb-2">
        <Card.Header>{category.name}</Card.Header>
        <Card.Body className="p-0">
          <ListGroup variant="flush">
            {category.items && category.items.map((item, itemIndex) => (
              <ListGroup.Item 
                key={itemIndex}
                action
                onClick={() => handleServiceSelect(category, item)}
                className="d-flex justify-content-between align-items-center"
              >
                <span>{item.name}</span>
                <Badge variant="primary">₹{item.price}</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    ))
  ) : (
    // New package headings rendering - only show headings
    renderPackageHeadings().map((packageGroup, index) => (
      <Card key={index} className="mb-2">
        <Card.Header 
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => handleSelectAllPackages(packageGroup.heading, packageGroup.items)}
        >
          <div>{packageGroup.heading}</div>
          <Badge variant="primary">{packageGroup.items.length} items</Badge>
        </Card.Header>
      </Card>
    ))
  )} */}
  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
  {formState.activeTab === 'services' ? (
    renderServiceCategories().map((category, index) => (
      <Card key={index} className="mb-2">
        <Card.Header>{category.name}</Card.Header>
        <Card.Body className="p-0">
          <ListGroup variant="flush">
            {category.items && category.items.map((item, itemIndex) => (
              <ListGroup.Item 
                key={itemIndex}
                action
                onClick={() => handleServiceSelect(category, item)}
                className="d-flex justify-content-between align-items-center"
              >
                <div>
                  <div>{item.name}</div>
                  {item.vendor && item.vendor.trim() !== '' && (
                    <small className="text-muted">Vendor: {item.vendor}</small>
                  )}
                </div>
                <Badge variant="primary">₹{item.price}</Badge>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    ))
  ) : (
    // Original package rendering code remains unchanged
    renderPackageHeadings().map((packageGroup, index) => (
      <Card key={index} className="mb-2">
        <Card.Header 
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={() => handleSelectAllPackages(packageGroup.heading, packageGroup.items)}
        >
          <div>{packageGroup.heading}</div>
          <Badge variant="primary">{packageGroup.items.length} items</Badge>
        </Card.Header>
      </Card>
    ))
  )}
</div>

                </Col>
              </Row>
            </div>
          </Container>
        </div>
      </div>
    </div>
  );
};

export default EditInvoice;